import { useState } from 'react';

import { productionApi } from 'api/production';
import { Card, CardContent, CardHeader, CardTitle } from 'shadcn/components/ui/Card';

import { useEditorMutation } from '../hooks/useEditorMutation';

import BannerEditorActions from './BannerEditorActions';
import BannerEditorForm from './BannerEditorForm';
import BannerEditorSummary from './BannerEditorSummary';

/**
 * Displays an editor for aisle banners.
 *
 * @param {string} [aisleID] - Parent aisle ID.
 * @param {Object} banner - Aisle banner data.
 * @param {Object} brandOptions - Brand options.
 * @param {Function} handleDeleteNewEditor - Callback function to delete a new banner editor.
 * @param {string} initialView - Initial view, defaults to 'summary'.
 * @param {Function} refetchBanners - Function to refetch banners from the parent component.
 */
export default function BannerEditor({
    aisleID,
    banner,
    brandOptions,
    handleDeleteNewEditor,
    initialView = 'summary',
    refetchBanners,
}) {
    const [view, setView] = useState(initialView);
    const [formData, setFormData] = useState(banner);
    const [imageErrors, setImageErrors] = useState({
        largeImage: null,
        smallImage: null,
    });
    const { executeMutation, isSaving } = useEditorMutation({ entityName: 'Banner', refetch: refetchBanners });

    const toggleView = () => {
        setView((prev) => (prev === 'summary' ? 'form' : 'summary'));
    };

    const handleChange = (e) => {
        setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleImageClear = () => {
        setFormData((prev) => ({ ...prev, imageUrl: '' }));
    };

    const handleImageUpload = async (file, imageType) => {
        try {
            const response = await productionApi.uploadStorefrontConfigImage(file);
            const imageUrl = response.data.imagePath;
            setFormData((prev) => ({ ...prev, [imageType]: imageUrl, [`${imageType}File`]: file }));
            setImageErrors({ ...imageErrors, [imageType]: null });
        } catch (e) {
            setImageErrors({ ...imageErrors, [imageType]: `Error: ${e.message}` });
        }
    };

    // The new Lettuce UI expects filenames for display, while the existing
    // banner logic still relies on binary files. We're supporting both formats
    // for now, until the endpoint is updated to accept filenames.
    const handleSave = async (e) => {
        e.preventDefault();
        await executeMutation({
            action: async () => {
                if (banner.id) {
                    const { largeImage, largeImageFile, smallImage, smallImageFile, taxonomyID, ...restFormData } =
                        formData;
                    const response = await productionApi.updateAisleBanner({
                        id: banner.id,
                        ...restFormData,
                        ...(largeImageFile && { largeImage: largeImageFile }),
                        ...(smallImageFile && { smallImage: smallImageFile }),
                        active: formData.active ? 'Yes' : 'No',
                        aisleID,
                    });
                    if (!response.status) {
                        throw new Error('Failed to update banner');
                    }
                } else {
                    const { largeImageFile, smallImageFile, ...restFormData } = formData;
                    const response = await productionApi.createAisleBanner({
                        ...restFormData,
                        active: formData.active ? 'Yes' : 'No',
                        aisleID,
                        largeImage: largeImageFile,
                        smallImage: smallImageFile,
                    });
                    if (!response.status) {
                        throw new Error('Failed to create banner');
                    }
                    handleDeleteNewEditor();
                }
            },
        });
    };

    const handleDelete = async () => {
        if (!banner.id) {
            handleDeleteNewEditor();
            return;
        }
        await executeMutation({
            action: async () => {
                const response = await productionApi.deleteAisleBanner(banner.id);
                if (!response.status) {
                    throw new Error(response.msg || 'Failed to delete banner');
                }
            },
            loading: 'Deleting banner...',
            success: 'Banner deleted successfully',
        });
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center justify-between">
                    {banner.bannerName ? (
                        <div className="flex items-center gap-1">
                            <h3>{banner.bannerName}</h3>
                            <span className="ml-2 text-sm text-muted-foreground">(H1 Banner)</span>
                            <span className="ml-2 font-normal text-sm text-muted-foreground">id: {banner.id}</span>
                        </div>
                    ) : (
                        <h3>H1 Banner</h3>
                    )}
                    <BannerEditorActions onDelete={handleDelete} toggleView={toggleView} view={view} />
                </CardTitle>
            </CardHeader>
            <CardContent>
                {view === 'summary' && (
                    <BannerEditorSummary data={banner} formData={formData} toggleView={toggleView} />
                )}
                {view === 'form' && (
                    <BannerEditorForm
                        aisleID={aisleID}
                        brandOptions={brandOptions}
                        data={banner}
                        formData={formData}
                        handleChange={handleChange}
                        handleImageClear={handleImageClear}
                        handleImageUpload={handleImageUpload}
                        handleSave={handleSave}
                        imageErrors={imageErrors}
                        isSaving={isSaving}
                    />
                )}
            </CardContent>
        </Card>
    );
}
